import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ScrollToTop } from './components/ScrollToTop';
import { RouteFallback } from './components/RouteFallback';
import { Home } from './routes/Home';

/**
 * Home is imported eagerly — it is the landing route, so deferring it would only
 * add a round trip to the page most visitors see first. Every other route is a
 * separate chunk fetched on navigation, which keeps the initial download to the
 * shell plus one page rather than all eighteen.
 *
 * The routes use named exports, so each import is mapped onto the `default` key
 * that `lazy()` expects.
 */
const About = lazy(() => import('./routes/About').then((m) => ({ default: m.About })));
const Services = lazy(() => import('./routes/Services').then((m) => ({ default: m.Services })));
const Training = lazy(() => import('./routes/Training').then((m) => ({ default: m.Training })));
const TrainingAccess = lazy(() =>
  import('./routes/TrainingAccess').then((m) => ({ default: m.TrainingAccess })),
);
const FormPlayer = lazy(() =>
  import('./routes/FormPlayer').then((m) => ({ default: m.FormPlayer })),
);
const Portal = lazy(() => import('./routes/Portal').then((m) => ({ default: m.Portal })));
const VerifyCertificate = lazy(() =>
  import('./routes/VerifyCertificate').then((m) => ({ default: m.VerifyCertificate })),
);
const Register = lazy(() => import('./routes/Register').then((m) => ({ default: m.Register })));
const GroupRegister = lazy(() =>
  import('./routes/GroupRegister').then((m) => ({ default: m.GroupRegister })),
);
const TrainingCheckout = lazy(() =>
  import('./routes/TrainingCheckout').then((m) => ({ default: m.TrainingCheckout })),
);
const CheckoutComplete = lazy(() =>
  import('./routes/CheckoutComplete').then((m) => ({ default: m.CheckoutComplete })),
);
const Bulletin = lazy(() => import('./routes/Bulletin').then((m) => ({ default: m.Bulletin })));
const BulletinDetail = lazy(() =>
  import('./routes/BulletinDetail').then((m) => ({ default: m.BulletinDetail })),
);
const BulletinSubscription = lazy(() =>
  import('./routes/BulletinSubscription').then((m) => ({ default: m.BulletinSubscription })),
);
const Contact = lazy(() => import('./routes/Contact').then((m) => ({ default: m.Contact })));
const Terms = lazy(() => import('./routes/Terms').then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import('./routes/Privacy').then((m) => ({ default: m.Privacy })));
const CookiePolicy = lazy(() =>
  import('./routes/CookiePolicy').then((m) => ({ default: m.CookiePolicy })),
);
const SignIn = lazy(() => import('./routes/SignIn').then((m) => ({ default: m.SignIn })));

export function App() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/training" element={<Training />} />
          <Route path="/training/checkout/complete" element={<CheckoutComplete />} />
          <Route path="/training/checkout/:slug" element={<TrainingCheckout />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/group" element={<GroupRegister />} />
          <Route path="/training-access/:token" element={<TrainingAccess />} />
          <Route path="/forms/:token" element={<FormPlayer />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/verify" element={<VerifyCertificate />} />
          <Route path="/bulletin" element={<Bulletin />} />
          <Route path="/bulletin/:slug" element={<BulletinDetail />} />
          <Route
            path="/bulletin-subscription/confirm"
            element={<BulletinSubscription action="confirm" />}
          />
          <Route
            path="/bulletin-subscription/unsubscribe"
            element={<BulletinSubscription action="unsubscribe" />}
          />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
